import { createSelector } from "reselect";

import type { AIAgentState } from "app/pages/sales-intelligence/modules/ai-agent/store";
import { selectSalesIntelligenceState } from "app/pages/sales-intelligence/store/selectors";
import { createStatePropertySelector } from "app/pages/workspace/sales/helpers";

import type { AiThreadMessage, HumanThreadMessage } from "../../types/thread";

import type { UserMessageData } from "./types";

const selectAiAgentState = createStatePropertySelector<AIAgentState>(selectSalesIntelligenceState("aiAgent"));
const selectChatState = selectAiAgentState("chat");

export const selectChatText = createSelector(selectChatState, ({ text }) => text);

export const selectMessageError = createSelector(
    [selectChatState, (_state, messageId: string) => messageId],
    ({ messageErrors }, messageId) => messageErrors[messageId],
);

export const selectChatMessages = createSelector(selectChatState, ({ messages }) => messages);

export const selectIsInitialChatState = createSelector(selectChatState, ({ isInitialChatState }) => isInitialChatState);

export const selectUserMessageCount = createSelector(selectChatState, ({ userMessageCount }) => userMessageCount);

/**
 * Factory function to create a typed selector for user message data
 * Use this in specific modules for strict typing
 * @template T - Additional properties beyond base message
 */
export const createTypedUserMessageDataSelector = <T extends UserMessageData = UserMessageData>() =>
    createSelector(selectChatState, ({ userMessageData }): T => userMessageData as T);

const selectHumanMessages = createSelector(selectChatMessages, (messages): HumanThreadMessage[] =>
    messages.filter((message): message is HumanThreadMessage => message.type === "human"),
);

export const selectLastHumanMessage = createSelector(selectChatMessages, (messages): HumanThreadMessage | undefined =>
    messages.findLast((message) => message.type === "human"),
);

export const selectLastAiMessage = createSelector(selectChatMessages, (messages): AiThreadMessage | undefined =>
    messages.findLast((message) => message.type === "ai"),
);

export const selectHumanMessagesCount = createSelector(selectHumanMessages, (messages) => messages.length);
